/* eslint-disable camelcase */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { Grid } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

import { Helper } from '../../../common/helpers/Helper';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { AppConfig } from '../../../AppConfig';
import { AppTheme } from '../../../styles/AppTheme';
import { SubEntityCard } from '../../../common/components/SubEntity/SubEntityCard';
import { SubEntityList } from '../../../common/components/SubEntity/SubEntityList';
import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { AppStyles } from '../../../styles/AppTheme';
import { ContentState, convertToRaw } from 'draft-js';
import { WhatsappUtils, buildWaNumberSelectOptions, extractCrmNumbersItems, getWaOrgAccountNumbers, withVendorProjectIdQueryParam } from './WhatsappUtils';

/** @param {string[]} fromNumbers */
export function buildWaTemplateFieldDef(fromNumbers, waNumberDetails) {
  return {
    title: 'WhatsApp Template',
    ctaButtonText: 'Save',
    steps: [
      {
        fields: [
          {
            key: 'wabaNumber',
            type: 'radio',
            label: 'WABA Number',
            tip: 'WhatsApp Business Account phone number',
            options: buildWaNumberSelectOptions(fromNumbers, waNumberDetails),
            v_required: { value: true, err: 'Select WABA number' },
            placement: 'left',
          },
          {
            key: 'title',
            type: 'edit_text',
            label: 'Name',
            tip: 'Lowercase letters, numbers, underscores (snake_case)',
            v_required: { value: true, err: 'Provide template name' },
            placement: 'right',
          },
          {
            key: 'iType',
            type: 'radio',
            label: 'Category',
            options: [
              { key: 'UTILITY', label: 'UTILITY' },
              { key: 'MARKETING', label: 'MARKETING' },
            ],
            v_required: { value: true, err: 'Select category' },
            placement: 'left',
          },
          {
            key: 'headerType',
            type: 'radio',
            label: 'Header',
            options: [
              { key: 'NONE', label: 'NONE' },
              { key: 'TEXT', label: 'TEXT' },
              { key: 'IMAGE', label: 'IMAGE' },
              { key: 'VIDEO', label: 'VIDEO' },
              { key: 'DOCUMENT', label: 'DOCUMENT' },
            ],
            v_required: { value: true, err: 'Select header type' },
            placement: 'right',
          },
          {
            key: 'headerText',
            type: 'edit_text',
            label: 'Header text',
            tip: 'Use {{1}}, {{2}}, … for variables',
            condition: { key: 'headerType', values: ['TEXT'] },
            v_required: { value: true, err: 'Provide header text' },
            placement: 'left',
          },
          {
            key: 'bodyRichText',
            type: 'rich_text',
            toolbarFlags: 'CLEAR_FORMATTING, FORMAT_BOLD, FORMAT_ITALIC, BULLETS',
            label: 'Body text',
            tip: 'Bold, italic, and bullets. List items are sent as lines starting with "- " (WhatsApp). Use Variables to insert {{1}}, {{2}}, …',
            rows: 6,
            variablePicker: {
              title: 'Variables',
              options: Array.from({ length: 15 }, (_, i) => {
                const n = i + 1
                return { key: String(n), value: `{{${n}}}`, text: `{{${n}}}` }
              }),
            },
            v_required: { value: true, err: 'Provide body text' },
            placement: 'left',
          },
          {
            key: 'footerText',
            type: 'edit_text',
            label: 'Footer text (optional)',
            tip: 'Plain text only — variables like {{1}} are not supported in the footer.',
            placement: 'left',
            v_required: { value: false },
          },
        ],
      },
    ],
  }
}

function waAddModalButtonSlotFields(slot) {
  const activeTypes = ['QUICK_REPLY', 'URL', 'PHONE']
  const notNone = { key: `button${slot}Type`, values: activeTypes }
  const prevVisible = slot === 1 ? null : { key: `button${slot - 1}Type`, values: activeTypes }
  return [
    {
      key: `button${slot}Type`,
      type: 'radio',
      label: `Button ${slot}`,
      tip:
        slot === 1
          ? 'Up to 3 buttons. Quick Reply: max 3 total. URL + Phone: max 2 combined. Labels max 20 characters.'
          : undefined,
      options: [
        { key: 'NONE', label: 'NONE' },
        { key: 'QUICK_REPLY', label: 'QUICK_REPLY' },
        { key: 'PHONE', label: 'PHONE' },
        { key: 'URL', label: 'URL' },
      ],
      v_required: { value: true, err: 'Select button type' },
      placement: slot % 2 === 1 ? 'left' : 'right',
      ...(prevVisible ? { condition: prevVisible } : {}),
    },
    {
      key: `button${slot}Text`,
      type: 'edit_text',
      label: `Button ${slot} text`,
      tip: 'Plain text only, max 20 characters (no variables supported).',
      condition: notNone,
      v_required: { value: true, err: 'Provide button text' },
      placement: slot % 2 === 1 ? 'right' : 'left',
    },
    {
      key: `button${slot}Url`,
      type: 'edit_text',
      label: `Button ${slot} URL`,
      tip: 'A single {{1}} may appear at the end of the URL.',
      condition: { key: `button${slot}Type`, value: 'URL' },
      v_required: { value: true, err: 'Provide URL' },
      placement: 'left',
    },
    {
      key: `button${slot}UrlVariable`,
      type: 'edit_text',
      label: `Button ${slot} URL variable example`,
      tip: 'Sample value for the variable at the end of the URL (if your URL contains a variable like {{1}})',
      condition: { key: `button${slot}Type`, value: 'URL' },
      v_required: { value: false },
      placement: 'right',
    },
    {
      key: `button${slot}Phone`,
      type: 'edit_text',
      label: `Button ${slot} phone`,
      tip: 'Phone number for the call button',
      condition: { key: `button${slot}Type`, value: 'PHONE' },
      v_required: { value: true, err: 'Provide phone number' },
      placement: 'left',
    },
  ]
}

/**
 * Public sample URLs for IMAGE / VIDEO / DOCUMENT headers in the Add Template modal.
 * Replace with real assets before relying on template submission.
 */
/*export const WA_ADD_MODAL_HEADER_MEDIA_PLACEHOLDER_URLS  = {
  IMAGE: '1637320776228/wa-template-example-image.jpg/a31231b0-3761-11f1-858d-9589a698df36.jpg',
  VIDEO: '1637320776228/c9b761f0-3761-11f1-858d-9589a698df36.mp4',
  DOCUMENT: '1637320776228/wa-template-example-document.pdf/90e63f40-3761-11f1-858d-9589a698df36.pdf',
}*/
export const WA_ADD_MODAL_HEADER_MEDIA_PLACEHOLDER_URLS  = {
  IMAGE: '1637320776228/a3811ff0-3c1e-11f1-a67f-8158b48ddb7f.jpg',
  VIDEO: '1637320776228/74e842e0-3c1e-11f1-9247-ef990ad08d37.mp4',
  DOCUMENT: '1637320776228/d2f6c870-3c1e-11f1-a67f-8158b48ddb7f.pdf',
}

/** Form definition for the list-page “Add Template” modal (submit in-dialog; no /add route). */
export function buildWaTemplateAddModalFieldDef(fromNumbers, waNumberDetails) {
  const fd = JSON.parse(JSON.stringify(buildWaTemplateFieldDef(fromNumbers, waNumberDetails)))
  fd.title = 'Add WhatsApp Template'
  fd.ctaButtonText = 'Submit'
  const fields = fd.steps[0].fields
  const idxHeaderText = fields.findIndex(f => f.key === 'headerText')
  fields.splice(idxHeaderText + 1, 0, {
    key: 'headerTextVariableExample',
    type: 'edit_text',
    label: 'Header Text Variable Example',
    tip: 'Sample for the header variable (if your header text contains a variable like {{1}})',
    condition: { key: 'headerType', value: 'TEXT' },
    v_required: { value: false },
    placement: 'right',
  })
  const idxBody = fields.findIndex(f => f.key === 'bodyRichText')
  fields.splice(idxBody + 1, 0, {
    key: 'bodyTextVariableExamples',
    type: 'edit_text',
    label: 'Body Text Variable Examples (pipe separated)',
    tip: 'Optional. Separate values with | (e.g. val1|val2). First maps to {{1}}, second to {{2}}, in order of variables in the body.',
    v_required: { value: false },
    placement: 'right',
  })
  const idxFooter = fields.findIndex(f => f.key === 'footerText')
  const buttonFields = [1, 2, 3].flatMap(n => waAddModalButtonSlotFields(n))
  fields.splice(idxFooter + 1, 0, ...buttonFields)
  fd.alwaysShowModalScrollbar = true
  return fd
}

/** Field definition shape; WABA options are filled by `buildWaTemplateFieldDef(waNumbers)`. */
export const watemplateFieldDef = buildWaTemplateFieldDef([])

export const buttonsFieldDef = {
  title: 'Button',
  ctaButtonText: 'Save',
  steps: [
    {
      fields: [
        {
          key: 'iType',
          type: 'radio',
          label: 'Type',
          options: [
            { key: 'QUICK_REPLY', label: 'QUICK_REPLY' },
            { key: 'URL', label: 'URL' },
            { key: 'PHONE', label: 'PHONE' },
          ],
          v_required: { value: true, err: 'Select button type' },
          placement: 'left',
        },
        {
          key: 'text',
          type: 'edit_text',
          label: 'Text',
          v_required: { value: true, err: 'Provide button text' },
          placement: 'right',
        },
        {
          key: 'url',
          type: 'edit_text',
          label: 'Url',
          tip: 'Optional dynamic segment: a single {{1}} at the end (e.g. https://example.com/t/{{1}}). Set its sample under Variable Examples.',
          condition: { key: 'iType', value: 'URL' },
          v_required: { value: true, err: 'Provide URL' },
          placement: 'left',
        },
        {
          key: 'phone_number',
          type: 'edit_text',
          label: 'Phone number',
          condition: { key: 'iType', value: 'PHONE' },
          v_required: { value: true, err: 'Provide phone number' },
          placement: 'left',
        },
      ],
    },
  ],
}

/** Stable id for HEADER sample media in `waTemplate.examples` (IMAGE / VIDEO / DOCUMENT). */
export const HEADER_MEDIA_EXAMPLE_ID = 'waExample_headerMedia'

/**
 * CDP hooks may return HTTP 200 with { respCode, message } for business-level errors.
 * User-facing text is only the top-level `message`; `errorDetail` is not used for display.
 * @param {object|null|undefined} data parsed JSON body
 * @returns {string|null} trimmed `message`, or null if absent / blank / not an error-shaped body
 */
export function hooksBridgeErrorMessage(data) {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return null
  const has = Object.prototype.hasOwnProperty
  const hasResp = has.call(data, 'respCode')
  const hasMsg = has.call(data, 'message')
  if (!hasResp && !hasMsg) return null
  const msg = hasMsg && data.message != null ? String(data.message).trim() : ''
  return msg || null
}

function cloneButtonsFieldDefForTemplate() {
  const fd = JSON.parse(JSON.stringify(buttonsFieldDef))
  return fd
}

function getButtonCounts(buttonsList) {
  const quickReplyCount = buttonsList.filter(b => b && b.iType === 'QUICK_REPLY').length
  const urlCount = buttonsList.filter(b => b && b.iType === 'URL').length
  const phoneCount = buttonsList.filter(b => b && b.iType === 'PHONE').length
  const ctaCount = buttonsList.filter(b => b && (b.iType === 'URL' || b.iType === 'PHONE' || b.iType === 'FLOW')).length
  return {
    totalCount: buttonsList.length,
    quickReplyCount,
    urlCount,
    phoneCount,
    ctaCount,
  }
}

function validateButtonMixLimits(buttonsList) {
  const { totalCount, quickReplyCount, urlCount, phoneCount, ctaCount } = getButtonCounts(buttonsList)
  if (quickReplyCount > 3) return 'Quick Reply allows at most 3 buttons.'
  if (phoneCount > 1) return 'At most 1 Phone button is allowed.'
  if (urlCount > 2) return 'At most 2 URL buttons are allowed.'
  if (ctaCount > 2) return 'URL, Phone and Flow buttons combined cannot be more than 2.'
  let seenQuick = false
  let seenNonQuickAfterQuick = false
  for (let i = 0; i < buttonsList.length; i++) {
    const b = buttonsList[i]
    const type = b && b.iType
    if (type === 'QUICK_REPLY') {
      if (seenNonQuickAfterQuick) return 'Quick Reply buttons must be grouped together.'
      seenQuick = true
    } else if (seenQuick) {
      seenNonQuickAfterQuick = true
    }
  }
  if (totalCount > 10) return 'A template can have at most 10 buttons in total.'
  return null
}

function collectVariableIndicesFromText(s) {
  if (!s || typeof s !== 'string') return []
  const out = []
  const re = /\{\{(\d+)\}\}/g
  let m
  while ((m = re.exec(s)) !== null) {
    out.push(parseInt(m[1], 10))
  }
  return out
}

function uniqueSortedVariableIndicesFromText(s) {
  const raw = collectVariableIndicesFromText(s)
  return [...new Set(raw)].sort((a, b) => a - b)
}

/** Draft.js JSON used when the body editor is empty. */
export function emptyWaBodyRichText() {
  return JSON.stringify({
    __azformatted: convertToRaw(ContentState.createFromText('')),
  })
}

/** Plain string → stored `bodyRichText` shape (`{ __azformatted }`). */
export function plainTextToWaBodyRichText(plain) {
  const t = plain == null ? '' : String(plain)
  return JSON.stringify({
    __azformatted: convertToRaw(ContentState.createFromText(t)),
  })
}

/** Concatenated block text + newlines; used for {{n}} detection (ignores formatting). */
export function richTextToPlainStringForVariables(richTextJson) {
  if (richTextJson == null || richTextJson === '') return ''
  try {
    const parsed = typeof richTextJson === 'string' ? JSON.parse(richTextJson) : richTextJson
    if (!parsed || !parsed.__azformatted || !Array.isArray(parsed.__azformatted.blocks)) return ''
    return parsed.__azformatted.blocks
      .map(b => (WhatsappUtils.draftBlockIsUnorderedListItem(b) ? `- ${b.text || ''}` : b.text || ''))
      .join('\n')
  } catch (e) {
    return ''
  }
}

/**
 * Converts stored `bodyRichText` (Draft `__azformatted`) to WhatsApp body string with * / _ markup.
 * Unordered list blocks become lines prefixed with "- " (WhatsApp bullet style).
 * Only bold and italic from the editor are honored; partially styled `{{n}}` tokens are normalized to full-token style.
 */
export function waRichTextToWhatsAppBodyText(richTextJson) {
  return WhatsappUtils.waRichTextToWhatsAppBodyText(richTextJson)
}

/** True if the same `{{n}}` appears more than once in body text (not allowed). */
export function bodyTextHasRepeatedVariableIndex(bodyText) {
  const raw = collectVariableIndicesFromText(bodyText || '')
  const counts = {}
  raw.forEach(n => {
    counts[n] = (counts[n] || 0) + 1
  })
  return Object.values(counts).some(c => c > 1)
}

export function exampleIdForHeaderTextVar(vIndex) {
  return `waEx_hdr_${vIndex}`
}

export function exampleIdForBodyVar(vIndex) {
  return `waEx_body_${vIndex}`
}

export function exampleIdForUrlButtonVar(buttonId, vIndex) {
  return `waEx_url_${buttonId}_${vIndex}`
}

export function exampleIdForButtonLabelVar(buttonId, vIndex) {
  return `waEx_btnTxt_${buttonId}_${vIndex}`
}

/**
 * Maps add-modal form values to internal `waTemplate` shape (buttons, examples).
 */
export function buildAddModalWaTemplateFromForm(formData) {
  const headerType = formData.headerType || 'NONE'
  const headerText = headerType === 'TEXT' ? String(formData.headerText || '') : ''
  const bodyRichText = formData.bodyRichText || emptyWaBodyRichText()
  const footerText = formData.footerText != null ? String(formData.footerText) : ''

  const buttons = {}
  const examples = {}

  for (let slot = 1; slot <= 3; slot++) {
    if (slot > 1) {
      const prev = formData[`button${slot - 1}Type`]
      if (!prev || prev === 'NONE') break
    }
    const bt = formData[`button${slot}Type`]
    if (!bt || bt === 'NONE') break
    const id = `btn_add_${slot}`
    if (bt === 'QUICK_REPLY') {
      buttons[id] = {
        id,
        iType: 'QUICK_REPLY',
        text: String(formData[`button${slot}Text`] || ''),
        url: '',
        phone_number: '',
      }
    } else if (bt === 'URL') {
      buttons[id] = {
        id,
        iType: 'URL',
        text: String(formData[`button${slot}Text`] || ''),
        url: String(formData[`button${slot}Url`] || ''),
        phone_number: '',
      }
    } else if (bt === 'PHONE') {
      buttons[id] = {
        id,
        iType: 'PHONE',
        text: String(formData[`button${slot}Text`] || ''),
        url: '',
        phone_number: String(formData[`button${slot}Phone`] || ''),
      }
    } else if (bt === 'FLOW') {
      buttons[id] = {
        id,
        iType: 'FLOW',
        text: String(formData[`button${slot}Text`] || ''),
        url: '',
        phone_number: '',
        flow_id: String(formData[`button${slot}FlowId`] || ''),
      }
    }
  }

  if (headerType === 'TEXT') {
    uniqueSortedVariableIndicesFromText(headerText).forEach(vi => {
      const sample = String(formData.headerTextVariableExample || '').trim()
      examples[exampleIdForHeaderTextVar(vi)] = {
        id: exampleIdForHeaderTextVar(vi),
        type: 'HEADER',
        format: 'text',
        vIndex: vi,
        text: sample,
      }
    })
  }

  const bodyPlain = richTextToPlainStringForVariables(bodyRichText)
  const examplesRaw = formData.bodyTextVariableExamples != null ? String(formData.bodyTextVariableExamples) : ''
  const parts = examplesRaw.split('|').map(s => s.trim())
  uniqueSortedVariableIndicesFromText(bodyPlain).forEach((vi, idx) => {
    const textVal = parts[idx] != null ? parts[idx] : ''
    examples[exampleIdForBodyVar(vi)] = {
      id: exampleIdForBodyVar(vi),
      type: 'BODY',
      format: 'text',
      vIndex: vi,
      text: textVal,
    }
  })

  Object.values(buttons).forEach(b => {
    if (b.iType !== 'URL' || !b.url) return
    const slotMatch = /^btn_add_(\d+)$/.exec(b.id || '')
    const slot = slotMatch ? parseInt(slotMatch[1], 10) : 1
    uniqueSortedVariableIndicesFromText(b.url).forEach(vi => {
      const ex = String(formData[`button${slot}UrlVariable`] || '').trim()
      examples[exampleIdForUrlButtonVar(b.id, vi)] = {
        id: exampleIdForUrlButtonVar(b.id, vi),
        type: 'URL',
        format: 'text',
        vIndex: vi,
        buttonId: b.id,
        text: ex,
      }
    })
  })

  const formatByHeader = { IMAGE: 'image', VIDEO: 'video', DOCUMENT: 'document' }
  const hf = formatByHeader[headerType]
  if (hf) {
    const mediaLink = String(WA_ADD_MODAL_HEADER_MEDIA_PLACEHOLDER_URLS[headerType] || '').trim()
    examples[HEADER_MEDIA_EXAMPLE_ID] = {
      id: HEADER_MEDIA_EXAMPLE_ID,
      type: 'HEADER',
      format: hf,
      media: '',
      mediaLink,
    }
  }

  return {
    title: String(formData.title || '').trim(),
    iType: formData.iType || 'UTILITY',
    wabaNumber: formData.wabaNumber || '',
    headerType,
    headerText,
    bodyRichText,
    footerText,
    buttons,
    examples,
  }
}

/**
 * Each variable placeholder row in Variable Examples (HEADER text, BODY, URL suffix, button label).
 * @returns {{ id: string, type: string, format: string, vIndex: number, buttonId?: string }[]}
 */
export function collectTemplateExampleSlots(wa) {
  const slots = []
  if (wa && wa.headerType === 'TEXT') {
    uniqueSortedVariableIndicesFromText(wa.headerText || '').forEach(vi => {
      slots.push({
        id: exampleIdForHeaderTextVar(vi),
        type: 'HEADER',
        format: 'text',
        vIndex: vi,
      })
    })
  }
  uniqueSortedVariableIndicesFromText(richTextToPlainStringForVariables(wa && wa.bodyRichText)).forEach(vi => {
    slots.push({
      id: exampleIdForBodyVar(vi),
      type: 'BODY',
      format: 'text',
      vIndex: vi,
    })
  })
  const buttons = wa && wa.buttons && typeof wa.buttons === 'object'
    ? Helper.sortDictArray(Helper.dictToArray(wa.buttons), 'id')
    : []
  buttons.forEach(b => {
    if (b.iType === 'URL') {
      uniqueSortedVariableIndicesFromText(b.url || '').forEach(vi => {
        slots.push({
          id: exampleIdForUrlButtonVar(b.id, vi),
          type: 'URL',
          format: 'text',
          vIndex: vi,
          buttonId: b.id,
        })
      })
    }
    uniqueSortedVariableIndicesFromText(b.text || '').forEach(vi => {
      slots.push({
        id: exampleIdForButtonLabelVar(b.id, vi),
        type: 'BUTTON',
        format: 'text',
        vIndex: vi,
        buttonId: b.id,
      })
    })
  })
  return slots
}

function countExampleSlotsByVIndex(slots) {
  const m = {}
  slots.forEach(s => {
    if (s.vIndex != null) m[s.vIndex] = (m[s.vIndex] || 0) + 1
  })
  return m
}

function legacyWaVarExampleText(prevExamples, vIndex) {
  const c = prevExamples[`waVar_${vIndex}`]
  if (c && c.format === 'text' && c.text != null && String(c.text).trim() !== '') {
    return String(c.text).trim()
  }
  return ''
}

/** WhatsApp dynamic URL buttons require the single variable at the end of the URL. */
export function validateUrlButtonVariableAtEnd(url) {
  if (!url || typeof url !== 'string' || countTemplatePlaceholders(url) === 0) return null
  const u = url.trim()
  if (!/\{\{\d+\}\}\s*$/.test(u)) {
    return 'URL button variables must be at the end of the URL (e.g. https://example.com/track/{{1}}).'
  }
  return null
}

/** Count of `{{n}}` placeholders in a string (WhatsApp header / URL allow at most one). */
export function countTemplatePlaceholders(s) {
  if (!s || typeof s !== 'string') return 0
  const m = s.match(/\{\{\d+\}\}/g)
  return m ? m.length : 0
}

function isHeaderSampleMediaDefined(comp) {
  if (!comp || comp.type !== 'HEADER') return false
  const fmt = comp.format || 'image'
  if (fmt === 'image' || fmt === 'video' || fmt === 'document') {
    return !!(comp.mediaLink && String(comp.mediaLink).trim())
      || !!(comp.media && String(comp.media).trim())
  }
  return false
}

export function isVariableExampleDefined(comp) {
  if (!comp) return false
  if (comp.type === 'HEADER' && comp.format === 'text') {
    const t = comp.text
    return t != null && String(t).trim() !== ''
  }
  if (comp.type === 'URL' || comp.type === 'BUTTON') {
    const t = comp.text
    return t != null && String(t).trim() !== ''
  }
  if (comp.type === 'HEADER') {
    return isHeaderSampleMediaDefined(comp)
  }
  if (comp.vIndex == null) return false
  const fmt = comp.format || 'text'
  if (fmt === 'image') {
    return !!(comp.mediaLink && String(comp.mediaLink).trim())
      || !!(comp.media && String(comp.media).trim())
  }
  if (fmt === 'video') {
    return !!(comp.mediaLink && String(comp.mediaLink).trim())
      || !!(comp.media && String(comp.media).trim())
  }
  if (fmt === 'document') {
    return !!(comp.mediaLink && String(comp.mediaLink).trim())
      || !!(comp.media && String(comp.media).trim())
  }
  const t = comp.text
  return t != null && String(t).trim() !== ''
}

/** @returns {string|null} error message or null */
export function validateVariableExamplesComplete(wa) {
  const examples = (wa && wa.examples) || {}
  const slots = collectTemplateExampleSlots(wa)
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]
    const comp = examples[slot.id]
    if (!isVariableExampleDefined(comp)) {
      const where = slot.type === 'URL' ? 'URL button' : slot.type
      return `Set an example for {{${slot.vIndex}}} (${where}) in Variable Examples.`
    }
  }
  if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(wa && wa.headerType)) {
    const h = examples[HEADER_MEDIA_EXAMPLE_ID]
    if (!isHeaderSampleMediaDefined(h)) {
      return 'Upload a sample for the HEADER in Variable Examples (required for this header type).'
    }
  }
  return null
}

export const WA_TEMPLATE_NAME_SNAKE_RE = /^[a-z][a-z0-9_]*$/

/** @returns {string|null} error message or null */
export function validateWaTemplateClientWa(wa) {
  if (!wa.wabaNumber) return 'Select a WABA number.'
  if (!wa.title || !WA_TEMPLATE_NAME_SNAKE_RE.test(wa.title)) {
    return 'Only lowercase letters, numbers and underscores are allowed in name (eg: sample_template_1).'
  }
  const bodyPlain = richTextToPlainStringForVariables(wa.bodyRichText)
  if (!bodyPlain || !String(bodyPlain).trim()) return 'Body is required.'
  if (bodyTextHasRepeatedVariableIndex(bodyPlain)) {
    return 'Body text cannot repeat the same variable (e.g. two {{1}} placeholders). Use {{1}}, {{2}}, … once each.'
  }
  if (wa.headerType === 'TEXT' && (!wa.headerText || !String(wa.headerText).trim())) {
    return 'Header text is required for a TEXT header.'
  }
  if (wa.headerType === 'TEXT' && countTemplatePlaceholders(wa.headerText) > 1) {
    return 'Header text may include at most one variable (e.g. a single {{1}}).'
  }
  const buttonsList = wa.buttons && typeof wa.buttons === 'object' ? Object.values(wa.buttons) : []
  const badUrlVar = buttonsList.find(b => b.iType === 'URL' && b.url && countTemplatePlaceholders(b.url) > 1)
  if (badUrlVar) {
    return 'URL buttons may include at most one variable in the URL (e.g. a single {{1}}).'
  }
  const badUrlEnd = buttonsList.find(
    b => b.iType === 'URL' && b.url && countTemplatePlaceholders(b.url) > 0
  )
  if (badUrlEnd) {
    const urlVarErr = validateUrlButtonVariableAtEnd(badUrlEnd.url)
    if (urlVarErr) return urlVarErr
  }
  if (wa.footerText && countTemplatePlaceholders(wa.footerText) > 0) {
    return 'Footer text does not support variables; remove {{1}}, {{2}}, etc.'
  }
  const buttonLimitsErr = validateButtonMixLimits(buttonsList)
  if (buttonLimitsErr) return buttonLimitsErr
  const examplesErr = validateVariableExamplesComplete(wa)
  if (examplesErr) return examplesErr
  return null
}

/** Add-modal: plain button labels only, max 20 characters. */
export function validateAddModalWaTemplate(wa) {
  const base = validateWaTemplateClientWa(wa)
  if (base) return base
  const buttonsList = wa.buttons && typeof wa.buttons === 'object' ? Object.values(wa.buttons) : []
  for (let i = 0; i < buttonsList.length; i++) {
    const b = buttonsList[i]
    const t = String(b.text || '')
    if (t.length > 20) return 'Button text must be at most 20 characters.'
    if (countTemplatePlaceholders(t) > 0) {
      return 'Button labels cannot use {{n}} variables in this dialog; use plain text.'
    }
    if (b.iType === 'FLOW' && !String(b.flow_id || '').trim()) {
      return 'Flow button requires a selected flow.'
    }
  }
  return null
}

function getFieldDefWATemplateVariable(current) {
  const fields = []
  if (current.type === 'HEADER' && current.format === 'text') {
    fields.push({
      key: 'text',
      type: 'edit_text',
      label: 'Header example text',
      tip: 'Sample for this HEADER variable when creating the template',
      multiline: true,
      v_required: { value: true, err: 'Provide example text' },
      placement: 'left',
    })
  } else if (current.type === 'URL' && current.format === 'text') {
    fields.push({
      key: 'text',
      type: 'edit_text',
      label: 'URL suffix example',
      tip: 'Sample value for the variable at the end of this button URL',
      multiline: true,
      v_required: { value: true, err: 'Provide example text' },
      placement: 'left',
    })
  } else if (current.type === 'BUTTON' && current.format === 'text') {
    fields.push({
      key: 'text',
      type: 'edit_text',
      label: 'Button label example',
      tip: 'Sample for this variable in the button label',
      multiline: true,
      v_required: { value: true, err: 'Provide example text' },
      placement: 'left',
    })
  } else if (current.type === 'HEADER' && current.format === 'image') {
    fields.push({
      key: 'media',
      linkName: 'mediaLink',
      type: 'edit_text',
      label: 'Header sample image',
      tip: 'Pick from assets or upload (PNG/JPEG, max 5MB)',
      imageHeight: 1920,
      mode: 'fit',
      v_required: { value: true, err: 'Provide sample image' },
      v_min: { value: 1, err: 'Invalid image' },
      v_max: { value: 100, err: 'Invalid image' },
      imageUpload: true,
      uploadType: 'media',
      uploadAccept: 'image/png, image/jpeg',
      fileSizeLimit: 5000000,
      placement: 'left',
    })
  } else if (current.type === 'HEADER' && current.format === 'video') {
    fields.push({
      key: 'media',
      linkName: 'mediaLink',
      label: 'Header sample video',
      hint: 'MP4, max 16MB',
      v_required: { value: true, err: 'Provide sample video' },
      placement: 'left',
      type: 'edit_text',
      subType: 'VIDEO',
      documentUpload: true,
      v_min: { value: 1, err: 'Invalid video' },
      v_max: { value: 100, err: 'Invalid video' },
      uploadType: 'media',
      uploadAccept: 'video/mp4',
      fileSizeLimit: 16000000,
    })
  } else if (current.type === 'HEADER' && current.format === 'document') {
    fields.push({
      key: 'media',
      linkName: 'mediaLink',
      label: 'Header sample document',
      hint: 'PDF for header (max 20MB)',
      v_required: { value: true, err: 'Provide sample document' },
      placement: 'left',
      type: 'edit_text',
      documentUpload: true,
      v_min: { value: 1, err: 'Invalid document' },
      v_max: { value: 100, err: 'Invalid document' },
      uploadType: 'media',
      uploadAccept: 'application/pdf',
      fileSizeLimit: 20000000,
    })
  } else if (current.format === 'image') {
    fields.push({
      key: 'media',
      linkName: 'mediaLink',
      type: 'edit_text',
      label: 'Image (PNG/JPEG, max 5MB, max 1920px height)',
      imageHeight: 1920,
      mode: 'fit',
      tip: 'Upload an image in PNG or JPEG format (max 5MB)',
      v_required: { value: true, err: 'Provide image' },
      v_min: { value: 1, err: 'Invalid image' },
      v_max: { value: 100, err: 'Invalid image' },
      imageUpload: true,
      uploadType: 'media',
      uploadAccept: 'image/png, image/jpeg',
      fileSizeLimit: 5000000,
      placement: 'left',
    })
  } else if (current.format === 'text') {
    fields.push({
      key: 'text',
      type: 'edit_text',
      label: 'Example text',
      tip: 'Sample value used for this variable when creating the template',
      multiline: true,
      v_required: { value: true, err: 'Provide example text' },
      placement: 'left',
    })
  } else if (current.format === 'video') {
    fields.push({
      key: 'media',
      label: 'Video (MP4, max 16MB file size)',
      hint: 'Upload a video in MP4 format',
      v_required: {
        value: true,
        err: 'Provide video',
      },
      placement: 'left',
      type: 'edit_text',
      subType: 'VIDEO',
      documentUpload: true,
      linkName: 'mediaLink',
      v_min: {
        value: 1,
        err: 'Invalid video',
      },
      v_max: {
        value: 100,
        err: 'Invalid video',
      },
      uploadType: 'media',
      uploadAccept: 'video/mp4',
      fileSizeLimit: 16000000,
    })
  } else if (current.format === 'document') {
    fields.push({
      key: 'media',
      linkName: 'mediaLink',
      label: 'Document',
      hint: 'Upload PDF document (max 20MB)',
      v_required: { value: true, err: 'Provide document' },
      placement: 'left',
      type: 'edit_text',
      documentUpload: true,
      v_min: { value: 1, err: 'Invalid document' },
      v_max: { value: 100, err: 'Invalid document' },
      uploadType: 'media',
      uploadAccept: 'application/pdf',
      fileSizeLimit: 20000000,
    })
  }

  return {
    title: 'Edit',
    ctaButtonText: 'Save',
    steps: [
      {
        fields,
      },
    ],
  }
}

function renderWaTemplateVariablesValue(current) {
  if (current && current.format === 'image' && current.mediaLink) {
    return (
      <div className={css(Styles.imageWrapper)}>
        <img
          src={current.mediaLink}
          alt={current.media || current.mediaLink || 'Preview'}
          className={css(Styles.imagePreview)}
        />
      </div>
    )
  }
  if (current.format === 'video' && current.mediaLink) {
    return (
      <div className={css(Styles.videoWrapper)}>
        <video
          src={current.mediaLink}
          controls
          controlsList='nodownload noplaybackrate'
          className={css(Styles.videoPreview)}
        />
      </div>
    )
  }
  if (current && current.format === 'document' && current.mediaLink) {
    return (
      <div>
        <a href={current.mediaLink} target='_blank' rel='noopener noreferrer' className={css(Styles.spanValue)}>
          Open document
        </a>
      </div>
    )
  }
  if (current && current.format === 'text' && current.text) {
    return (
      <div>
        <span className={css(Styles.spanValue)}>{current.text}</span>
      </div>
    )
  }

  return (
    <div>
      <span className={css(Styles.spanValue)}>
        {current.text || current.mediaLink || current.media || '[Not Set]'}
      </span>
    </div>
  )
}

/** Public URL for a variable-example media row (header sample or body media). */
export function exampleMediaUrlFromExample(comp) {
  if (!comp) return ''
  if (comp.mediaLink && String(comp.mediaLink).trim()) {
    return String(comp.mediaLink).trim()
  }
  if (comp.media && String(comp.media).trim()) {
    return String(comp.media).trim()
  }
  // TODO: resolve asset id (comp.media) to a public URL via org assets / CDP if link is absent
  return ''
}

function getExamplesDict(wa) {
  return (wa && wa.examples && typeof wa.examples === 'object') ? wa.examples : {}
}

function exampleTextForExampleId(wa, exampleId, viFallback) {
  const comp = getExamplesDict(wa)[exampleId]
  if (comp && comp.format === 'text' && comp.text != null && String(comp.text).trim() !== '') {
    return String(comp.text).trim()
  }
  return `example_${viFallback}`
}

/**
 * Maps editor state to Meta Cloud API–style template payload (components array).
 * Variable examples use scoped ids (`waEx_hdr_*`, `waEx_body_*`, `waEx_url_*`, `waEx_btnTxt_*`). Media header uses `HEADER_MEDIA_EXAMPLE_ID` (`example.header_handle`).
 * @param {object} wa
 */
export function waTemplateToActualWaTemplate(wa) {
  const components = []
  const examples = getExamplesDict(wa)
  const headerSample = examples[HEADER_MEDIA_EXAMPLE_ID]
  const headerMediaRaw = exampleMediaUrlFromExample(headerSample)
  const headerMediaUrl = headerMediaRaw
    ? `https://d1bpnn2a5id540.cloudfront.net/${String(headerMediaRaw).replace(/^\//, '')}`
    : ''
  if (wa.headerType === 'TEXT' && wa.headerText && String(wa.headerText).trim()) {
    const headerComp = {
      type: 'HEADER',
      format: 'TEXT',
      text: wa.headerText,
    }
    const headerVars = uniqueSortedVariableIndicesFromText(wa.headerText)
    if (headerVars.length > 0) {
      headerComp.example = {
        header_text: headerVars.map(vi => exampleTextForExampleId(wa, exampleIdForHeaderTextVar(vi), vi)),
      }
    }
    components.push(headerComp)
  } else if (wa.headerType && wa.headerType !== 'NONE' && wa.headerType !== 'TEXT') {
    const headerComp = {
      type: 'HEADER',
      format: wa.headerType,
    }
    if (headerMediaUrl) {
      headerComp.example = { header_handle: [headerMediaUrl] }
    }
    components.push(headerComp)
  }

  const bodyPlainForVars = richTextToPlainStringForVariables(wa.bodyRichText)
  const bodyComp = {
    type: 'BODY',
    text: waRichTextToWhatsAppBodyText(wa.bodyRichText),
  }
  const bodyVarIndices = uniqueSortedVariableIndicesFromText(bodyPlainForVars)
  if (bodyVarIndices.length > 0 && examples && typeof examples === 'object') {
    const bodyExamples = bodyVarIndices.map(vi => exampleTextForExampleId(wa, exampleIdForBodyVar(vi), vi))
    bodyComp.example = { body_text: [bodyExamples] }
  }
  components.push(bodyComp)

  if (wa.footerText && String(wa.footerText).trim()) {
    components.push({
      type: 'FOOTER',
      text: wa.footerText,
    })
  }

  if (wa.buttons && typeof wa.buttons === 'object') {
    const list = Helper.sortDictArray(Helper.dictToArray(wa.buttons), 'id')
    const metaButtons = list.map(b => {
      if (b.iType === 'QUICK_REPLY') {
        return { type: 'QUICK_REPLY', text: b.text }
      }
      if (b.iType === 'URL') {
        const urlRow = { type: 'URL', text: b.text, url: b.url }
        const urlVarIndices = uniqueSortedVariableIndicesFromText(b.url || '')
        if (urlVarIndices.length > 0) {
          urlRow.example = urlVarIndices.map(vi =>
            exampleTextForExampleId(wa, exampleIdForUrlButtonVar(b.id, vi), vi)
          )
        }
        return urlRow
      }
      if (b.iType === 'PHONE') {
        return { type: 'PHONE_NUMBER', text: b.text, phone_number: b.phone_number }
      }
      if (b.iType === 'FLOW') {
        const flowId = String(b.flow_id || '').trim()
        return {
          type: 'FLOW',
          text: b.text,
          flow_id: flowId,
          flow_action: 'NAVIGATE',
        }
      }
      return null
    }).filter(Boolean)
    if (metaButtons.length > 0) {
      components.push({ type: 'BUTTONS', buttons: metaButtons })
    }
  }

  const out = {
    name: wa.title,
    language: 'en',
    category: wa.iType,
    components,
  }
  return out
}

/** Maps stored `waTemplate` fields to form keys for SubEntityCard / edit modal. */
export function prepareWaTemplateFormItem(wa) {
  if (!wa) return {}
  const o = { ...wa }
  delete o.headerText
  delete o.bodyText

  if (wa.headerType === 'TEXT') {
    o.headerText = wa.headerText
  }
  if (!o.bodyRichText && typeof wa.bodyText === 'string' && wa.bodyText) {
    o.bodyRichText = plainTextToWaBodyRichText(wa.bodyText)
  }
  if (!o.bodyRichText) {
    o.bodyRichText = emptyWaBodyRichText()
  }
  return o
}

export function sortVariableExampleItems(examples) {
  const items = Helper.dictToArray(examples || {})
  const rank = row => {
    const id = String(row.id || '')
    if (id === HEADER_MEDIA_EXAMPLE_ID) return [0, 0, id]
    if (id.startsWith('waEx_hdr_')) {
      const n = parseInt(id.replace(/^waEx_hdr_/, ''), 10) || 0
      return [1, n, id]
    }
    if (id.startsWith('waEx_body_')) {
      const n = parseInt(id.replace(/^waEx_body_/, ''), 10) || 0
      return [2, n, id]
    }
    if (id.startsWith('waEx_url_')) return [3, id]
    if (id.startsWith('waEx_btnTxt_')) return [4, id]
    return [9, id]
  }
  return items.sort((a, b) => {
    const ra = rank(a)
    const rb = rank(b)
    for (let i = 0; i < Math.max(ra.length, rb.length); i++) {
      const x = ra[i] != null ? ra[i] : ''
      const y = rb[i] != null ? rb[i] : ''
      if (x < y) return -1
      if (x > y) return 1
    }
    return 0
  })
}

function initialWaTemplateFromLocation(location) {
  const draft = location && location.state && location.state.initialWaTemplateDraft
  if (!draft || typeof draft !== 'object') {
    return {
      title: '',
      iType: 'UTILITY',
      wabaNumber: '',
      headerType: 'NONE',
      headerText: '',
      bodyRichText: emptyWaBodyRichText(),
      footerText: '',
      buttons: {},
      examples: {},
    }
  }
  const headerType = draft.headerType || 'NONE'
  return {
    title: draft.title || '',
    iType: draft.iType || 'UTILITY',
    wabaNumber: draft.wabaNumber || '',
    headerType,
    headerText: headerType === 'TEXT' ? (draft.headerText || '') : '',
    bodyRichText: draft.bodyRichText || emptyWaBodyRichText(),
    footerText: draft.footerText || '',
    buttons: (draft.buttons && typeof draft.buttons === 'object') ? draft.buttons : {},
    examples: (draft.examples && typeof draft.examples === 'object') ? draft.examples : {},
  }
}
  export function whatsAppBodyTextToWaBodyRichText(text) {
    const rawText = text == null ? '' : String(text)
    const blocks = rawText.split('\n').map(line => {
      const inlineStyleRanges = []
      let cleanText = ''
      let boldStart = null
      let italicStart = null

      for (let i = 0; i < line.length; i++) {
        const ch = line[i]

        if (ch === '*') {
          if (boldStart == null) {
            boldStart = cleanText.length
          } else {
            inlineStyleRanges.push({
              offset: boldStart,
              length: cleanText.length - boldStart,
              style: 'BOLD',
            })
            boldStart = null
          }
          continue
        }

        if (ch === '_') {
          if (italicStart == null) {
            italicStart = cleanText.length
          } else {
            inlineStyleRanges.push({
              offset: italicStart,
              length: cleanText.length - italicStart,
              style: 'ITALIC',
            })
            italicStart = null
          }
          continue
        }

        cleanText += ch
      }

      return {
        text: cleanText,
        inlineStyleRanges,
      }
    })

    const contentState = ContentState.createFromText(blocks.map(b => b.text).join('\n'))
    const raw = convertToRaw(contentState)

    raw.blocks = raw.blocks.map((block, idx) => ({
      ...block,
      inlineStyleRanges: blocks[idx].inlineStyleRanges,
    }))

    return JSON.stringify({
      __azformatted: raw,
    })
  }
export class WhatsappTemplateDetail extends BaseEntityPageSlixta {
  constructor(props) {
    super(props, { assetPicker: true })
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'Add WhatsApp Template'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.breadCrumbs = [
      { title: 'WhatsApp templates', to: this.baseRoute() + '/whatsapp-templates', type: 'button', icon: 'arrow_back_ios' },
    ]
    this.toFetch = [
      { key: 'waAccounts', ms: this.ms, method: 'GET', paths: ['miscs', 'get-wa-accounts'], queryParams: { orgId: this.orgId } },
      { key: 'crmnumbers', ms: this.ms, method: 'GET', paths: ['crmnumbers'], queryParams: { orgId: this.orgId } },
    ]
    this.apiData = {
      orgId: this.orgId,
    }
    this.pageKey = this.pageTitle
    this.noAdd = true
    this.state = {
      ...this.state,
      waTemplate: initialWaTemplateFromLocation(this.props.location),
    }
    this.variableExamplesHeaders = {
      Index: current => current.__index + 1,
      Type: current => (
        <div>
          <span className={css(Styles.spanValue)}>{(current.type || '').toUpperCase()}</span>
        </div>
      ),
      Format: current => {
        const vi = current.vIndex
        const base = (current.format || '').toUpperCase() + (vi != null ? ` {{${vi}}}` : '')
        return (
          <div>
            <span className={css(Styles.spanValue)}>{base}</span>
          </div>
        )
      },
      Value: current => renderWaTemplateVariablesValue(current),
    }
    this.buttonsTableHeadersMap = {
      Type: current => current.iType,
      Text: current => current.text,
      'Url / Phone': current => {
        if (current.iType === 'URL') return current.url || ''
        if (current.iType === 'PHONE') return current.phone_number || ''
        return ''
      },
    }
  }

  onCompleteAction() {
    this.setState({ modalOpen: false })
  }

  syncWaTemplateExamplesFromTemplate() {
    this.setState(prev => {
      const wa = prev.waTemplate
      const slots = collectTemplateExampleSlots(wa)
      const prevExamples = wa.examples || {}
      const viSlotCount = countExampleSlotsByVIndex(slots)
      const nextExamples = {}
      slots.forEach(slot => {
        const prev = prevExamples[slot.id]
        const base = {
          id: slot.id,
          type: slot.type,
          format: slot.format,
          vIndex: slot.vIndex,
          ...(slot.buttonId != null ? { buttonId: slot.buttonId } : {}),
        }
        if (prev && prev.id === slot.id) {
          nextExamples[slot.id] = {
            ...base,
            text: prev.text != null ? prev.text : '',
            media: prev.media,
            mediaLink: prev.mediaLink,
          }
        } else {
          let text = ''
          if (viSlotCount[slot.vIndex] === 1) {
            text = legacyWaVarExampleText(prevExamples, slot.vIndex)
          }
          nextExamples[slot.id] = { ...base, text }
        }
      })
      const formatByHeader = { IMAGE: 'image', VIDEO: 'video', DOCUMENT: 'document' }
      const headerFmt = formatByHeader[wa.headerType]
      if (headerFmt) {
        const prevH = prevExamples[HEADER_MEDIA_EXAMPLE_ID]
        if (prevH && prevH.type === 'HEADER' && prevH.format === headerFmt) {
          nextExamples[HEADER_MEDIA_EXAMPLE_ID] = prevH
        } else {
          nextExamples[HEADER_MEDIA_EXAMPLE_ID] = {
            id: HEADER_MEDIA_EXAMPLE_ID,
            type: 'HEADER',
            format: headerFmt,
          }
        }
      }
      if (JSON.stringify(nextExamples) === JSON.stringify(prevExamples)) return null
      return { waTemplate: { ...wa, examples: nextExamples } }
    })
  }

  async onFetchSuccess(results) {
    this.waAccounts = this.getResult(results, 'waAccounts')
    this.crmNumbers = extractCrmNumbersItems(this.getResult(results, 'crmnumbers'))
    const waOrgAccounts = this.waAccounts && this.waAccounts[this.orgId]
    const { fromNumbers, waNumberDetails } = getWaOrgAccountNumbers(waOrgAccounts)
    this.waNumberDetails = waNumberDetails
    this.waTemplateFieldDef = buildWaTemplateFieldDef(fromNumbers, waNumberDetails)
    this.headerActions = [
      {
        label: 'Submit template',
        tooltip: 'Create template via Meta',
        icon: 'send',
        type: 'icon-label',
        onClick: () => this.openSubmitConfirmModal(),
      },
    ]
    this.syncWaTemplateExamplesFromTemplate()
  }

  openSubmitConfirmModal() {
    this.formDefinition = {
      title: 'Submit template',
      description: 'Creates this template on the selected WABA using the Meta template format.',
      ctaButtonText: 'Submit',
      formType: 'edit',
      steps: [{ title: 'Submit', fields: [] }],
    }
    this.setState({
      modalOpen: true,
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: this.ms,
        paths: ['hooks'],
        queryParams: {
          orgId: this.orgId,
          bridgePath: 'whatsapptemplates',
        },
      }),
      apiData: { _submitWaTemplateToMeta: true },
    })
  }

  validateWaTemplateClient() {
    return validateWaTemplateClientWa(this.state.waTemplate)
  }

  openModalForEditWaTemplate = () => {
    const fd = JSON.parse(JSON.stringify(this.waTemplateFieldDef))
    fd.formType = 'edit'
    Helper.populateFormDefinitionValues(fd, prepareWaTemplateFormItem(this.state.waTemplate))
    this.formDefinition = fd
    this.setState({
      modalOpen: true,
      apiData: { waTemplateCardEdit: true },
      apiMethod: 'POST',
      apiEndPoint: '',
    })
  }

  openModalForAddButton = () => {
    const { buttons } = this.state.waTemplate
    const buttonList = buttons && typeof buttons === 'object' ? Object.values(buttons) : []
    const { totalCount, quickReplyCount, ctaCount } = getButtonCounts(buttonList)
    if (totalCount >= 10) {
      this.showMessage('A template can have at most 10 buttons in total.')
      return
    }
    if (quickReplyCount >= 3 && ctaCount >= 2) {
      this.showMessage('Button limits reached: max 3 Quick Reply and max 2 URL + Phone combined.')
      return
    }
    const id = `btn_${Date.now()}`
    const defaultItem = {
      id,
      iType: quickReplyCount < 3 ? 'QUICK_REPLY' : 'URL',
      text: '',
      url: '',
      phone_number: '',
    }
    const fd = cloneButtonsFieldDefForTemplate()
    fd.formType = 'add'
    Helper.deleteFormDefinitionValues(fd)
    Helper.populateFormDefinitionValues(fd, defaultItem)
    this.formDefinition = fd
    this.setState({
      modalOpen: true,
      apiData: { waButtonAdd: true, waButtonId: id },
      apiMethod: 'POST',
      apiEndPoint: '',
    })
  }

  openModalForEditButton = (current) => {
    const fd = cloneButtonsFieldDefForTemplate()
    fd.formType = 'edit'
    Helper.populateFormDefinitionValues(fd, current)
    this.formDefinition = fd
    this.setState({
      modalOpen: true,
      apiData: { waButtonEdit: true, waButtonId: current.id },
      apiMethod: 'POST',
      apiEndPoint: '',
    })
  }

  deleteWaButton = (current) => {
    const buttons = { ...this.state.waTemplate.buttons }
    delete buttons[current.id]
    this.setState({
      waTemplate: {
        ...this.state.waTemplate,
        buttons,
      },
    }, () => this.syncWaTemplateExamplesFromTemplate())
  }

  variableExamplesActions = current => {
    const actions = []
    actions.push(
      <Tooltip title='Edit example' placement='top-start' key={current.id + 'editTT'}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={() => this.openModalForEditWaVariable(current)}
          >
            edit
          </i>
        </span>
      </Tooltip>
    )
    return actions
  }

  openModalForEditWaVariable = current => {
    const copy = JSON.parse(JSON.stringify(current))
    this.formDefinition = getFieldDefWATemplateVariable(copy)
    this.formDefinition.formType = 'edit'
    Helper.populateFormDefinitionValues(this.formDefinition, copy)
    this.setState({
      modalOpen: true,
      apiData: { waVarEdit: true, waVarId: current.id },
      apiMethod: 'POST',
      apiEndPoint: '',
    })
  }

  async performWaTemplateHookSubmit(caller) {
    const err = this.validateWaTemplateClient()
    if (err) {
      caller.setState({
        inProcess: false,
        success: false,
        message: err,
      })
      return
    }
    const actualWaTemplate = waTemplateToActualWaTemplate(this.state.waTemplate)
    const wabaNumber = this.state.waTemplate.wabaNumber
    try {
      const data = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'POST',
        paths: ['hooks'],
        queryParams: withVendorProjectIdQueryParam({
          orgId: this.orgId,
          bridgePath: 'whatsapptemplates',
        }, wabaNumber, this.crmNumbers),
        jsonBody: {
          fromNumber: wabaNumber,
          template: actualWaTemplate,
        },
      })
      const bridgeErr = hooksBridgeErrorMessage(data)
      if (bridgeErr) {
        caller.setState({
          inProcess: false,
          success: false,
          message: bridgeErr,
        })
        return
      }
      this.setState({ modalOpen: false })
      caller.setState({
        inProcess: false,
        success: false,
        message: '',
      })
      const listPath = `${this.baseRoute()}/whatsapp-templates`
      this.showMessage(
        'The template was submitted successfully.',
        () => {
          if (this.props.history && this.props.history.push) {
            this.props.history.push(listPath)
          } else {
            window.location.assign(listPath)
          }
        },
        'Success'
      )
    } catch (e) {
      const errorMessage = Helper.getErrorMsg(e)
      caller.setState({
        inProcess: false,
        success: false,
        message: errorMessage,
      })
    }
  }

  async customSubmit(options) {
    const { formData, caller } = options
    if (formData.waTemplateCardEdit) {
      delete formData.waTemplateCardEdit
      const title = formData.title
      if (!title || !WA_TEMPLATE_NAME_SNAKE_RE.test(title)) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'Name must be lowercase snake_case (a–z, 0–9, _).',
        })
        return
      }
      const prev = this.state.waTemplate
      const headerTextVal = formData.headerType === 'TEXT'
        ? (formData.headerText != null ? formData.headerText : '')
        : ''
      if (formData.headerType === 'TEXT' && countTemplatePlaceholders(headerTextVal) > 1) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'Header text may include at most one variable (e.g. a single {{1}}).',
        })
        return
      }
      const footerVal = formData.footerText != null ? formData.footerText : ''
      if (countTemplatePlaceholders(footerVal) > 0) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'Footer text does not support variables; remove {{1}}, {{2}}, etc.',
        })
        return
      }
      const bodyPlainSave = richTextToPlainStringForVariables(formData.bodyRichText)
      if (!bodyPlainSave || !String(bodyPlainSave).trim()) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'Body is required.',
        })
        return
      }
      if (bodyTextHasRepeatedVariableIndex(bodyPlainSave)) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'Body text cannot repeat the same variable (e.g. two {{1}} placeholders).',
        })
        return
      }
      const next = {
        ...prev,
        wabaNumber: formData.wabaNumber,
        title: formData.title,
        iType: formData.iType,
        headerType: formData.headerType,
        headerText: headerTextVal,
        bodyRichText: formData.bodyRichText,
        footerText: formData.footerText != null ? formData.footerText : prev.footerText,
        buttons: { ...prev.buttons },
        examples: prev.examples || {},
      }
      this.setState({ waTemplate: next, modalOpen: false }, () => this.syncWaTemplateExamplesFromTemplate())
      caller.setState({ inProcess: false, success: true, message: '' })
      return
    }
    if (formData.waButtonAdd) {
      const id = formData.waButtonId || this.state.apiData.waButtonId
      delete formData.waButtonAdd
      delete formData.waButtonId
      if (formData.iType === 'URL' && formData.url && countTemplatePlaceholders(formData.url) > 1) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'URL buttons may include at most one variable in the URL (e.g. a single {{1}}).',
        })
        return
      }
      if (formData.iType === 'URL' && formData.url) {
        const urlVarErr = validateUrlButtonVariableAtEnd(formData.url)
        if (urlVarErr) {
          caller.setState({ inProcess: false, success: false, message: urlVarErr })
          return
        }
      }
      const row = {
        id,
        iType: formData.iType,
        text: formData.text,
        url: formData.url,
        phone_number: formData.phone_number,
      }
      const candidateButtons = { ...this.state.waTemplate.buttons, [id]: row }
      const buttonLimitsErr = validateButtonMixLimits(Object.values(candidateButtons))
      if (buttonLimitsErr) {
        caller.setState({ inProcess: false, success: false, message: buttonLimitsErr })
        return
      }
      this.setState({
        waTemplate: {
          ...this.state.waTemplate,
          buttons: candidateButtons,
        },
        modalOpen: false,
      }, () => this.syncWaTemplateExamplesFromTemplate())
      caller.setState({ inProcess: false, success: true, message: '' })
      return
    }
    if (formData.waButtonEdit) {
      const id = formData.waButtonId || this.state.apiData.waButtonId
      delete formData.waButtonEdit
      delete formData.waButtonId
      if (formData.iType === 'URL' && formData.url && countTemplatePlaceholders(formData.url) > 1) {
        caller.setState({
          inProcess: false,
          success: false,
          message: 'URL buttons may include at most one variable in the URL (e.g. a single {{1}}).',
        })
        return
      }
      if (formData.iType === 'URL' && formData.url) {
        const urlVarErr = validateUrlButtonVariableAtEnd(formData.url)
        if (urlVarErr) {
          caller.setState({ inProcess: false, success: false, message: urlVarErr })
          return
        }
      }
      const row = {
        id,
        iType: formData.iType,
        text: formData.text,
        url: formData.url,
        phone_number: formData.phone_number,
      }
      const buttons = { ...this.state.waTemplate.buttons, [id]: row }
      const buttonLimitsErr = validateButtonMixLimits(Object.values(buttons))
      if (buttonLimitsErr) {
        caller.setState({ inProcess: false, success: false, message: buttonLimitsErr })
        return
      }
      this.setState({
        waTemplate: { ...this.state.waTemplate, buttons },
        modalOpen: false,
      }, () => this.syncWaTemplateExamplesFromTemplate())
      caller.setState({ inProcess: false, success: true, message: '' })
      return
    }
    if (formData.waVarEdit) {
      const id = formData.waVarId || this.state.apiData.waVarId
      delete formData.waVarEdit
      delete formData.waVarId
      const raw = JSON.parse(JSON.stringify(formData))
      const wa = this.state.waTemplate
      const prevExamples = { ...(wa.examples || {}) }
      const prev = prevExamples[id] || {}
      let merged
      const isHeaderMedia = id === HEADER_MEDIA_EXAMPLE_ID
        || (prev.type === 'HEADER' && ['image', 'video', 'document'].includes(prev.format))
      if (isHeaderMedia) {
        merged = {
          id,
          type: 'HEADER',
          format: prev.format || 'image',
          media: raw.media,
          mediaLink: raw.mediaLink,
        }
      } else {
        merged = {
          ...prev,
          id,
          type: prev.type || 'BODY',
          format: prev.format || 'text',
          vIndex: prev.vIndex,
          buttonId: prev.buttonId,
          text: raw.text,
          media: raw.media,
          mediaLink: raw.mediaLink,
        }
      }
      this.setState({
        waTemplate: {
          ...wa,
          examples: { ...prevExamples, [id]: merged },
        },
        modalOpen: false,
      })
      caller.setState({ inProcess: false, success: true, message: '' })
      return
    }
    if (formData._submitWaTemplateToMeta) {
      delete formData._submitWaTemplateToMeta
      await this.performWaTemplateHookSubmit(caller)
      return
    }
    return caller.submit.call(caller, formData, true)
  }

  renderAboveTable() {
    const wa = this.state.waTemplate
    const buttonItems = Helper.sortDictArray(Helper.dictToArray(wa.buttons || {}), 'id')
    const varItems = sortVariableExampleItems(wa.examples)

    return (
      <div className={css(Styles.containerNarrow)}>
        {this.waTemplateFieldDef && (
          <Grid container>
            <Grid item xs={12} sm={12}>
              <SubEntityCard
                renderFieldsRowwise
                title='Template'
                formDefinition={this.waTemplateFieldDef}
                item={prepareWaTemplateFormItem(wa)}
                openModalForEdit={this.openModalForEditWaTemplate}
              />
            </Grid>
          </Grid>
        )}
        <Grid container>
          <Grid item xs={12} sm={12}>
            <SubEntityList
              title='Buttons'
              tableHeadersMap={this.buttonsTableHeadersMap}
              items={buttonItems}
              subEntityActions={current => [
                <Tooltip title='Edit' placement='top-start' key={`${current.id}-edit`}>
                  <span className={css(AppStyles.entityTableLinkIcon, Styles.actionIcon)}>
                    <i
                      className='material-icons-outlined'
                      onClick={() => this.openModalForEditButton(current)}
                    >
                      edit
                    </i>
                  </span>
                </Tooltip>,
                <Tooltip title='Delete' placement='top-start' key={`${current.id}-del`}>
                  <span className={css(AppStyles.entityTableLinkIcon, Styles.actionIcon)}>
                    <i
                      className='material-icons-outlined'
                      onClick={() => this.deleteWaButton(current)}
                    >
                      delete
                    </i>
                  </span>
                </Tooltip>,
              ]}
              openModalForAdd={this.openModalForAddButton}
              fetchState={this.state.fetchState}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={12}>
            <SubEntityList
              title='Variable Examples'
              tableHeadersMap={this.variableExamplesHeaders}
              items={varItems}
              subEntityActions={this.variableExamplesActions}
              noAdd
              fetchState={this.state.fetchState}
            />
          </Grid>
        </Grid>
      </div>
    )
  }
}

const Styles = StyleSheet.create({
  containerNarrow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: AppTheme.viewPort,
    paddingBottom: 50,
    width: '100%',
  },
  spanValue: {
    fontSize: 14,
  },
  imagePreview: {
    width: '100px',
    height: 'auto',
    display: 'block',
  },
  videoPreview: {
    width: '100px',
    height: 'auto',
    display: 'block',
  },
  imageWrapper: {
    display: 'block',
  },
  videoWrapper: {
    display: 'block',
  },
  actionIcon: {
    cursor: 'pointer',
  },
})
